import { useEffect, useMemo, useRef, useState } from "react";
import type {
  BadgeColor,
  CellType,
  ColumnDef,
  EditableRow as EditableRowType,
  EditableWorkstream,
} from "../types";
import { uid } from "../utils";
import { AddColumnForm } from "./AddColumnForm";
import { ColumnHeader } from "./ColumnHeader";
import { ConfirmDialog } from "./ConfirmDialog";
import { EditableRow } from "./EditableRow";

interface Props {
  ws: EditableWorkstream;
  onUpdate: (next: EditableWorkstream) => void;
}

// Preferred widths for the fixed-purpose columns. Auto table layout treats
// these as hints and ignores them once the table overflows, which is exactly
// why the first column's sticky offset is measured rather than assumed: an
// offset wider than the real handle column shoves the pinned cell sideways,
// over its neighbour.
const HANDLE_COLUMN_PX = 84;
const FIRST_COLUMN_PX = 200;
const DELETE_COLUMN_PX = 32;

function emptyCells(columns: ColumnDef[]): Record<string, string> {
  const cells: Record<string, string> = {};
  for (const col of columns) {
    cells[col.id] = col.type === "status" ? "Not Started" : "";
  }
  return cells;
}

// Normalize a raw cell value to what the renderer actually shows. The "status"
// type falls back to "Not Started" when empty, so the filter should match that.
function normalizeForType(value: string, type: ColumnDef["type"]): string {
  if (type === "status" && !value) return "Not Started";
  return value;
}

// Walk parent + children, returning every cell value seen for a given column,
// normalized to match the rendered display.
function collectValues(
  rows: EditableRowType[],
  col: ColumnDef,
): string[] {
  const out: string[] = [];
  for (const r of rows) {
    out.push(normalizeForType(r.cells[col.id] ?? "", col.type));
    if (r.children) {
      for (const c of r.children) {
        out.push(normalizeForType(c.cells[col.id] ?? "", col.type));
      }
    }
  }
  return out;
}

// A row is visible iff for every active filter, its (normalized) cell value
// is in the allowed set. Sub-rows respect parent visibility.
function applyFilters(
  rows: EditableRowType[],
  filters: Map<string, Set<string>>,
  columns: ColumnDef[],
): EditableRowType[] {
  if (filters.size === 0) return rows;
  const typeById = new Map(columns.map((c) => [c.id, c.type]));
  return rows.filter((r) => {
    for (const [colId, allowed] of filters) {
      const type = typeById.get(colId);
      if (!type) continue;
      const v = normalizeForType(r.cells[colId] ?? "", type);
      if (!allowed.has(v)) return false;
    }
    return true;
  });
}

export function EditableTable({ ws, onUpdate }: Props) {
  const columns = ws.columns ?? [];
  const rows = ws.rows ?? [];
  const [editMode, setEditMode] = useState(false);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [pendingDeleteColId, setPendingDeleteColId] = useState<string | null>(null);

  // Per-column filter state: colId -> set of allowed values. Absent key = no filter.
  // Local-only by design (per spec): filters are a personal lens, not shared via Cosmos.
  const [filters, setFilters] = useState<Map<string, Set<string>>>(new Map());

  // ---- Two synchronized horizontal scrollbars (top + bottom) ------------
  // Lets users scroll horizontally without scrolling the whole page down to
  // reach the bottom scrollbar.
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [overflowsHorizontally, setOverflowsHorizontally] = useState(false);

  // Actual rendered width of the row-handle column, which is where the first
  // data column has to pin.
  const handleCellRef = useRef<HTMLTableCellElement>(null);
  const [handleWidth, setHandleWidth] = useState(HANDLE_COLUMN_PX);

  useEffect(() => {
    const el = handleCellRef.current;
    if (!el) return;
    const measure = () => setHandleWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure the bottom container's scrollWidth so the top phantom div has
  // the same width and produces a matching scrollbar.
  useEffect(() => {
    const el = bottomScrollRef.current;
    if (!el) return;
    const measure = () => {
      setContentWidth(el.scrollWidth);
      setOverflowsHorizontally(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [columns.length, rows.length]);

  // Keep both scroll positions in lock-step. The flag avoids recursive sync.
  const isSyncingRef = useRef(false);
  function syncScroll(source: "top" | "bottom") {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    const src = source === "top" ? topScrollRef.current : bottomScrollRef.current;
    const dst = source === "top" ? bottomScrollRef.current : topScrollRef.current;
    if (src && dst && dst.scrollLeft !== src.scrollLeft) {
      dst.scrollLeft = src.scrollLeft;
    }
    // Release on next tick so the destination's onScroll doesn't bounce back.
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  }

  // ---- Row mutations ----------------------------------------------------
  function updateRow(updated: EditableRowType) {
    onUpdate({
      ...ws,
      rows: rows.map((r) => (r.id === updated.id ? updated : r)),
    });
  }

  function deleteRow(id: string) {
    onUpdate({ ...ws, rows: rows.filter((r) => r.id !== id) });
  }

  function moveRow(idx: number, dir: -1 | 1) {
    const next = [...rows];
    const ni = idx + dir;
    if (ni < 0 || ni >= next.length) return;
    [next[idx], next[ni]] = [next[ni], next[idx]];
    onUpdate({ ...ws, rows: next });
  }

  function addRow() {
    onUpdate({
      ...ws,
      rows: [...rows, { id: uid(), cells: emptyCells(columns) }],
    });
  }

  // ---- Column mutations -------------------------------------------------
  function addColumn(col: ColumnDef) {
    onUpdate({ ...ws, columns: [...columns, col] });
    setShowAddColumn(false);
  }

  function renameColumn(id: string, label: string) {
    onUpdate({
      ...ws,
      columns: columns.map((c) => (c.id === id ? { ...c, label } : c)),
    });
  }

  function changeColumnTypeOrOptions(
    id: string,
    next: {
      type: CellType;
      options?: string[];
      optionColors?: Record<string, BadgeColor>;
    },
  ) {
    onUpdate({
      ...ws,
      columns: columns.map((c) => {
        if (c.id !== id) return c;
        const updated: ColumnDef = { ...c, type: next.type };
        if (next.type === "badge") {
          updated.options = next.options ?? [];
          updated.optionColors = next.optionColors ?? {};
        } else {
          // Non-badge types don't carry options or their colors.
          delete updated.options;
          delete updated.optionColors;
        }
        return updated;
      }),
    });
    // If the column was previously filtered, drop the filter — its options have
    // changed and the stored allowed-set may no longer make sense.
    if (filters.has(id)) {
      const nextFilters = new Map(filters);
      nextFilters.delete(id);
      setFilters(nextFilters);
    }
  }

  function removeColumn(id: string) {
    function stripCell(r: EditableRowType): EditableRowType {
      const { [id]: _drop, ...rest } = r.cells;
      void _drop;
      const next: EditableRowType = { id: r.id, cells: rest };
      if (r.children) next.children = r.children.map(stripCell);
      return next;
    }
    onUpdate({
      ...ws,
      columns: columns.filter((c) => c.id !== id),
      rows: rows.map(stripCell),
    });
    // Also drop any active filter on this column.
    if (filters.has(id)) {
      const nextFilters = new Map(filters);
      nextFilters.delete(id);
      setFilters(nextFilters);
    }
  }

  function moveColumn(idx: number, dir: -1 | 1) {
    const next = [...columns];
    const ni = idx + dir;
    if (ni < 0 || ni >= next.length) return;
    [next[idx], next[ni]] = [next[ni], next[idx]];
    onUpdate({ ...ws, columns: next });
  }

  // ---- Filter handling --------------------------------------------------
  function setColumnFilter(colId: string, next: Set<string> | null) {
    const nextFilters = new Map(filters);
    if (next === null) nextFilters.delete(colId);
    else nextFilters.set(colId, next);
    setFilters(nextFilters);
  }

  function clearAllFilters() {
    setFilters(new Map());
  }

  // Build value-count list for EVERY column (used by the FilterPopover).
  // Memoized because we recompute on every render and rows can be large.
  const valuesByColumn = useMemo(() => {
    const out = new Map<string, Array<{ value: string; count: number }>>();
    for (const col of columns) {
      const counts = new Map<string, number>();
      for (const v of collectValues(rows, col)) {
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      const sorted = Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => {
          if (a.value === "") return 1;
          if (b.value === "") return -1;
          // Numeric sort when both look like numbers; otherwise lexical.
          const an = Number(a.value);
          const bn = Number(b.value);
          if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
          return a.value.localeCompare(b.value);
        });
      out.set(col.id, sorted);
    }
    return out;
  }, [columns, rows]);

  const visibleRows = useMemo(
    () => applyFilters(rows, filters, columns),
    [rows, filters, columns],
  );
  const hiddenCount = rows.length - visibleRows.length;

  // Rows are addressed by their position in the underlying list, not in the
  // filtered view, so ranks and the move controls stay correct while filtering.
  const indexById = useMemo(
    () => new Map(rows.map((r, i) => [r.id, i])),
    [rows],
  );

  const pendingCol = pendingDeleteColId
    ? columns.find((c) => c.id === pendingDeleteColId)
    : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end gap-3">
        {filters.size > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-xs text-blue-600 hover:underline"
          >
            Clear {filters.size} filter{filters.size === 1 ? "" : "s"}
          </button>
        )}
        <button
          onClick={() => {
            setEditMode((v) => !v);
            setShowAddColumn(false);
          }}
          className={`text-xs px-3 py-1 rounded-lg transition-colors ${
            editMode
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "text-slate-500 hover:bg-slate-100"
          }`}
        >
          {editMode ? "Done editing columns" : "Edit columns"}
        </button>
      </div>

      {/* Top scrollbar — a thin phantom that mirrors the table's horizontal scroll,
          so wide tables can be panned without scrolling to the bottom bar first.
          Always-show-scrollbar forces the bar visible for mouse users on macOS.
          Hidden entirely when the table fits, where it would just be a gray strip. */}
      {overflowsHorizontally && (
        <div
          ref={topScrollRef}
          onScroll={() => syncScroll("top")}
          className="always-show-scrollbar overflow-x-auto overflow-y-hidden rounded-t-2xl ring-1 ring-slate-200 bg-slate-50"
          style={{ height: 14 }}
        >
          <div style={{ width: contentWidth, height: 1 }} />
        </div>
      )}

      {/* The table gets its own bounded scroll pane so the sticky header pins to
          the top of the table rather than scrolling away with the page. */}
      <div
        className={`ring-1 ring-slate-200 bg-white shadow-sm overflow-hidden rounded-b-2xl ${
          overflowsHorizontally ? "-mt-2" : "rounded-t-2xl"
        }`}
      >
        <div
          ref={bottomScrollRef}
          onScroll={() => syncScroll("bottom")}
          className="always-show-scrollbar overflow-auto max-h-[calc(100vh-17rem)]"
        >
          <table className="w-full text-sm">
            <colgroup>
              <col style={{ width: HANDLE_COLUMN_PX }} />
              {columns.map((col, i) => (
                <col
                  key={col.id}
                  style={i === 0 ? { width: FIRST_COLUMN_PX } : undefined}
                />
              ))}
              <col style={{ width: DELETE_COLUMN_PX }} />
              {editMode && <col />}
            </colgroup>
            <thead className="border-b border-slate-200">
              <tr className="bg-slate-50">
                <th
                  ref={handleCellRef}
                  className="sticky left-0 top-0 z-30 px-2 py-3 bg-slate-50"
                />
                {columns.map((col, i) => (
                  <ColumnHeader
                    key={col.id}
                    column={col}
                    editMode={editMode}
                    isFirst={i === 0}
                    isLast={i === columns.length - 1}
                    stickyLeft={i === 0 ? handleWidth : undefined}
                    onRename={(label) => renameColumn(col.id, label)}
                    onRemove={() => setPendingDeleteColId(col.id)}
                    onMoveLeft={() => moveColumn(i, -1)}
                    onMoveRight={() => moveColumn(i, 1)}
                    onChangeType={(next) => changeColumnTypeOrOptions(col.id, next)}
                    values={valuesByColumn.get(col.id) ?? []}
                    filterSelected={filters.get(col.id) ?? null}
                    onFilterChange={(next) => setColumnFilter(col.id, next)}
                  />
                ))}
                <th className="sticky top-0 z-20 w-8 px-2 py-3 bg-slate-50" />
                {editMode && (
                  <th className="sticky top-0 z-20 px-2 py-3 text-left bg-slate-50">
                    <button
                      onClick={() => setShowAddColumn((v) => !v)}
                      className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                    >
                      + Add column
                    </button>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, i) => {
                const idx = indexById.get(row.id) ?? i;
                return (
                  <EditableRow
                    key={row.id}
                    row={row}
                    columns={columns}
                    onUpdate={updateRow}
                    onDelete={() => deleteRow(row.id)}
                    onMoveUp={() => moveRow(idx, -1)}
                    onMoveDown={() => moveRow(idx, 1)}
                    isFirst={idx === 0}
                    isLast={idx === rows.length - 1}
                    firstColumnLeft={handleWidth}
                    rank={idx + 1}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Kept outside the scroll pane so "Add row" stays reachable on long tables. */}
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
          <button
            onClick={addRow}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-blue-600 transition-colors"
          >
            <span className="text-base leading-none">+</span> Add row
          </button>
          <div className="text-[11px] text-slate-400 italic flex items-center gap-3">
            {hiddenCount > 0 && (
              <span>
                Showing {visibleRows.length} of {rows.length} rows ({hiddenCount} hidden by filter)
              </span>
            )}
            {editMode && (
              <span>
                Click any column header to rename. &#9881; for type/options. × to remove.
              </span>
            )}
          </div>
        </div>
      </div>

      {showAddColumn && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center bg-slate-900/30 pt-32"
          onClick={() => setShowAddColumn(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <AddColumnForm
              onAdd={addColumn}
              onCancel={() => setShowAddColumn(false)}
            />
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingCol}
        title="Delete column?"
        message={
          pendingCol
            ? `Remove "${pendingCol.label}" from this workstream? All data in this column will be deleted for every row. This affects every viewer in real time.`
            : ""
        }
        confirmLabel="Delete column"
        destructive
        onConfirm={() => {
          if (pendingDeleteColId) removeColumn(pendingDeleteColId);
          setPendingDeleteColId(null);
        }}
        onCancel={() => setPendingDeleteColId(null)}
      />
    </div>
  );
}
